// Space weather in the viewer's controls: the SPACE WEATHER entry in the
// Explore group (#space-weather-btn), its card (Kp now and the day ahead,
// the solar wind, the regions and the week's biggest flare, what the
// viewer is drawing from), the "Aurora now" and "Sun today" views, the
// #aurora and #space-weather links, and a banner while a storm of Kp 6 or
// more is under way. The data and the live drawing are in viewer.js.
//
//   initSpaceWeatherUI({ showView(view), canShow(), showMagnetosphere() })
//   view: { body: 'earth' | 'sun', cam: { az, el, dist }, ms, speed }

import { onSpaceWeather, liveAuroraState, liveSunState } from './viewer.js';
import { gScale, kpAt, kpMax } from './parse.js';
import { DIPOLE_POLE, gmstDeg, ovalEdge, sunPosition } from './geomag.js';
import { earthFromSun, equatorialToScene } from './sunframe.js';
import { COMPACT_LAYOUT_QUERY } from '../mobiledevice.js';

const DEG = Math.PI / 180;
const BANNER_KP = 6;

const CSS = `
.sw-card { position: fixed; bottom: 156px; left: 50%; transform: translateX(-50%); z-index: 51; width: min(460px, calc(100vw - 32px));
  box-sizing: border-box; display: flex; flex-direction: column; gap: 8px; padding: 14px 16px; background: #0a0d13;
  border: none; border-radius: 12px; font-family: 'Inter', sans-serif; color: #ece7da; }
.sw-card[hidden], .sw-banner[hidden] { display: none; }
.sw-head { display: flex; align-items: center; gap: 8px; }
.sw-tag { font-size: 0.8rem; font-weight: 500; color: #ece7da; }
.sw-dot { width: 6px; height: 6px; border-radius: 50%; background: #e0a851; }
.sw-card.is-live .sw-dot { background: #7dd3a8; }
.sw-x { margin-left: auto; width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center; background: none; border: 0; border-radius: 8px;
  color: #a8a396; font-size: 20px; line-height: 1; cursor: pointer; padding: 0; margin-right: -8px; }
.sw-x:hover { color: #ece7da; background: #141922; }
.sw-x:focus-visible, .sw-btn:focus-visible, .sw-link:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.sw-kp { display: flex; align-items: baseline; gap: 10px; }
.sw-kp b { font-size: 1.6rem; font-weight: 500; color: #ece7da; font-variant-numeric: tabular-nums; }
.sw-kp span { font-size: 0.8rem; color: #a8a396; }
.sw-g { font-size: 0.8rem; font-weight: 500; color: #7dd3a8; }
.sw-g.hot { color: #f29a90; }
.sw-stats { display: flex; flex-wrap: wrap; gap: 3px 14px; font-size: 0.8rem; color: #a8a396; font-variant-numeric: tabular-nums; }
.sw-note { font-size: 0.74rem; line-height: 1.45; color: #a8a396; }
.sw-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; padding-top: 6px; }
.sw-btn { background: #141922; border: none; color: #ece7da; font-family: inherit; font-size: 0.8rem; font-weight: 500;
  min-height: 36px; padding: 0 12px; border-radius: 8px; cursor: pointer; transition: background-color 0.15s ease; }
.sw-btn:hover { background: #1b212c; color: #ece7da; }
.sw-link { font-size: 0.8rem; color: #ece7da; text-decoration: underline; text-decoration-color: #4a5263; text-underline-offset: 3px; }
.sw-link:hover { text-decoration-color: #ece7da; }
.sw-banner { position: fixed; top: 72px; left: 50%; transform: translateX(-50%); z-index: 50; width: min(560px, calc(100vw - 32px));
  box-sizing: border-box; display: flex; align-items: center; gap: 10px; padding: 8px 8px 8px 14px; background: #15302a; border: none;
  border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 0.84rem; color: #ece7da; }
.sw-banner p { margin: 0; flex: 1; line-height: 1.4; }
.sw-banner strong { color: #ece7da; font-weight: 500; }
.sw-banner .sw-btn { background: #1b3b33; }
.sw-banner .sw-btn:hover { background: #22483f; }
body.fly-mode .sw-card, body.scale-mode .sw-card, body.documentary-mode .sw-card, body.mode-screensaver .sw-card, body.rocketry-mode .sw-card,
body.eclipse-mode .sw-card, body.photo-mode-clean .sw-card, body.warp-active .sw-card, body.game-mode .sw-card, body.lesson-mode .sw-card,
body.fly-mode .sw-banner, body.scale-mode .sw-banner, body.documentary-mode .sw-banner, body.mode-screensaver .sw-banner, body.rocketry-mode .sw-banner,
body.eclipse-mode .sw-banner, body.photo-mode-clean .sw-banner, body.warp-active .sw-banner, body.game-mode .sw-banner, body.lesson-mode .sw-banner { display: none !important; }
/* The viewer's phone layout, sideways phones included: the card stops under the distance box. */
@media ${COMPACT_LAYOUT_QUERY} { .sw-banner { top: 76px; } .sw-card { bottom: 150px; max-height: min(52vh, calc(100vh - 230px)); overflow: auto; } }
`;

function el(tag, attrs = {}, kids = []) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'text') n.textContent = v;
    else if (k.startsWith('on')) n.addEventListener(k.slice(2), v);
    else if (v !== null && v !== undefined && v !== false) n.setAttribute(k, v === true ? '' : v);
  }
  for (const c of [].concat(kids)) if (c) n.append(c);
  return n;
}

const fmtKp = (k) => (Math.round(k * 10) / 10).toFixed(1);
const fmtDay = (iso) => new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });

// Camera directions in the scene's axes (az, el as src/ui/viewlink.js reads
// them) from an equatorial unit vector.
function camFrom(eq, dist) {
  const [x, y, z] = equatorialToScene(eq);
  return { az: Math.atan2(z, x) / DEG, el: Math.asin(Math.max(-1, Math.min(1, y))) / DEG, dist };
}

// Over the oval's midnight sector (30 degrees from the magnetic pole toward
// the anti-sunward side) of the hemisphere in more darkness, so the night
// half of the oval faces the camera.
export function auroraCamera(ms) {
  const sun = sunPosition(ms);
  const north = sun.dec <= 0;
  const pole = north ? DIPOLE_POLE : { lat: -DIPOLE_POLE.lat, lon: DIPOLE_POLE.lon + 180 };
  const fixed = (lat, lon) => [Math.cos(lat * DEG) * Math.cos(lon * DEG), Math.cos(lat * DEG) * Math.sin(lon * DEG), Math.sin(lat * DEG)];
  const p = fixed(pole.lat, pole.lon);
  const a = fixed(-sun.lat, sun.lon + 180);
  const along = a[0] * p[0] + a[1] * p[1] + a[2] * p[2];
  const m = a.map((c, i) => c - along * p[i]);
  const ml = Math.hypot(...m) || 1;
  const v = p.map((c, i) => c * Math.cos(30 * DEG) + (m[i] / ml) * Math.sin(30 * DEG));
  const g = gmstDeg(ms) * DEG;
  const eq = [v[0] * Math.cos(g) - v[1] * Math.sin(g), v[0] * Math.sin(g) + v[1] * Math.cos(g), v[2]];
  const l = Math.hypot(...eq);
  return camFrom(eq.map((c) => c / l), 4.2);
}

// From the Earth's side, so the disc is the one SDO sees.
export const sunCamera = (ms) => camFrom(earthFromSun(ms), 4.8);

export function initSpaceWeatherUI({ showView, canShow = () => true, showMagnetosphere = null }) {
  if (typeof document === 'undefined' || document.getElementById('sw-css')) return null;
  document.head.append(el('style', { id: 'sw-css', text: CSS }));
  let data = null;

  const card = el('section', { class: 'sw-card', hidden: true, 'aria-label': 'Space weather' });
  const banner = el('div', { class: 'sw-banner', hidden: true, role: 'status' });
  document.body.append(card, banner);
  const btn = document.getElementById('space-weather-btn');

  const show = (body, extra = {}) => {
    if (!canShow()) return;
    const ms = extra.ms ?? Date.now();
    showView({ body, ms, speed: extra.speed ?? 'real', cam: body === 'sun' ? sunCamera(ms) : auroraCamera(ms) });
  };

  function render() {
    const ms = Date.now();
    const kpNow = data && data.aurora ? kpAt(data.aurora.kp, ms) : null;
    const ahead = data && data.aurora ? kpMax(data.aurora.kp, ms, ms + 24 * 3600000) : null;
    const wind = data && data.aurora && data.aurora.wind ? data.aurora.wind.latest : null;
    const reg = data && data.sun && data.sun.regions;
    const flare = data && data.sun && data.sun.flares && data.sun.flares[0];
    const a = liveAuroraState();
    const s = liveSunState();
    card.classList.toggle('is-live', Boolean(data && data.source === 'live'));
    const g = kpNow ? gScale(kpNow.kp) : null;
    const rows = [
      el('div', { class: 'sw-head' }, [el('span', { class: 'sw-dot' }), el('span', { class: 'sw-tag', text: data ? (data.source === 'live' ? 'Space weather, live from NOAA' : 'Space weather, saved copy') : 'Space weather, loading' }),
        el('button', { class: 'sw-x', type: 'button', 'aria-label': 'Close', text: '×', onclick: () => toggle(false) })]),
    ];
    if (kpNow) {
      rows.push(el('div', { class: 'sw-kp' }, [el('b', { text: `Kp ${fmtKp(kpNow.kp)}` }), el('span', { text: kpNow.kind === 'observed' ? 'observed' : kpNow.kind === 'estimated' ? 'estimated now' : 'forecast' }),
        g ? el('span', { class: `sw-g${kpNow.kp >= BANNER_KP ? ' hot' : ''}`, text: `${g} storm` }) : null,
        ahead ? el('span', { text: `next 24 h: up to ${fmtKp(ahead.kp)}` }) : null]));
      rows.push(el('div', { class: 'sw-note', text: `At Kp ${fmtKp(kpNow.kp)} the oval's edge is near ${Math.round(ovalEdge(kpNow.kp))}° geomagnetic latitude (NOAA's rule of thumb), and it can be seen low on the horizon a few hundred km beyond.` }));
    }
    const stats = [];
    if (wind && wind.speed !== null) stats.push(`Solar wind ${Math.round(wind.speed)} km/s`);
    if (wind && wind.density !== null) stats.push(`${wind.density} p/cm³`);
    if (wind && wind.bz !== null) stats.push(`Bz ${wind.bz > 0 ? '+' : ''}${wind.bz} nT`);
    if (data && data.sun && data.sun.xray && data.sun.xray.cls) stats.push(`X-ray ${data.sun.xray.cls}`);
    if (reg) stats.push(`${reg.regions.length} numbered regions`);
    if (stats.length) rows.push(el('div', { class: 'sw-stats' }, stats.map((t) => el('span', { text: t }))));
    if (flare) {
      rows.push(el('div', { class: 'sw-stats' }, [el('span', { text: `Latest M or X flare: ${flare.cls}, ${fmtDay(flare.max)}${flare.region ? `, region ${flare.region}` : ''}` }),
        flare.region ? el('button', { class: 'sw-btn', type: 'button', text: 'Show at its peak', title: 'The clock goes to the flare\'s peak, paused, and the camera to the Sun', onclick: () => show('sun', { ms: Date.parse(flare.max), speed: 0 }) }) : null]));
    }
    const why = a.live
      ? (a.shape === 'ovation' ? 'The aurora is drawn from NOAA\'s OVATION nowcast and Kp.' : `The aurora follows Kp for the clock's time (${a.kind}).`)
      : 'The clock is away from the data, so the aurora is the model.';
    const whySun = s.live ? `The Sun shows today's ${s.regions} NOAA regions${s.flare ? `, region ${s.flare} flaring` : ''}.` : 'The Sun shows modelled spots.';
    rows.push(el('div', { class: 'sw-note', text: `${why} ${whySun}` }));
    rows.push(el('div', { class: 'sw-actions' }, [
      el('button', { class: 'sw-btn', type: 'button', text: 'Aurora now', onclick: () => show('earth') }),
      el('button', { class: 'sw-btn', type: 'button', text: 'Sun today', onclick: () => show('sun') }),
      showMagnetosphere ? el('button', { class: 'sw-btn', type: 'button', text: 'Magnetosphere', title: 'The Earth\'s magnetic field, its boundaries and the Van Allen belts for this solar wind', onclick: () => { if (canShow()) { toggle(false); showMagnetosphere(); } } }) : null,
      el('a', { class: 'sw-link', href: '/aurora/', text: 'Aurora tonight' }),
      el('a', { class: 'sw-link', href: '/space-weather/', text: 'Space weather' }),
    ]));
    rows.push(el('div', { class: 'sw-note', text: 'Data: NOAA Space Weather Prediction Center; NASA SDO.' }));
    card.replaceChildren(...rows);

    // The storm banner, once a day per storm level unless dismissed.
    const key = kpNow && kpNow.kp >= BANNER_KP ? `sw-banner-${new Date(ms).toISOString().slice(0, 10)}-${g}` : '';
    let dismissed = false;
    try { dismissed = key && localStorage.getItem(key) === '1'; } catch { /* storage off */ }
    banner.hidden = !key || dismissed || !card.hidden;
    if (key && !banner.hidden) {
      banner.replaceChildren(
        el('p', {}, [el('strong', { text: `${g} geomagnetic storm, Kp ${fmtKp(kpNow.kp)}. ` }), `Aurora may reach about ${Math.round(ovalEdge(kpNow.kp))}° geomagnetic latitude tonight.`]),
        el('button', { class: 'sw-btn', type: 'button', text: 'See it', onclick: () => { show('earth'); dismiss(); } }),
        el('a', { class: 'sw-link', href: '/aurora/', text: 'Can I see it?' }),
        el('button', { class: 'sw-x', type: 'button', 'aria-label': 'Dismiss', text: '×', onclick: dismiss }),
      );
    }
    function dismiss() { try { localStorage.setItem(key, '1'); } catch { /* storage off */ } banner.hidden = true; }
  }

  function toggle(on = card.hidden) {
    card.hidden = !on;
    if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    render();
  }
  if (btn) btn.addEventListener('click', () => toggle());
  onSpaceWeather((d) => { data = d; render(); });
  // The card's lines about what is drawn follow the clock.
  setInterval(() => { if (!card.hidden) render(); }, 5000);

  return {
    open: () => toggle(true),
    // #aurora and #space-weather (and #sun-today): open the card and the view.
    fromHash(id) {
      if (id === 'aurora') { toggle(true); show('earth'); return true; }
      if (id === 'space-weather' || id === 'sun-today') { toggle(true); if (id === 'sun-today') show('sun'); return true; }
      return false;
    },
  };
}
